import { SplashScreen } from '@capacitor/splash-screen';

import { useAuthStore } from '@/stores/auth';

import { isStoreBuild } from './platform';

/**
 * Оболочка приложения из магазина: отметка «это приложение» и заставка до готовности (24.09.2026, сборка 61).
 *
 * ⚠️ ТОЛЬКО В ПРИЛОЖЕНИИ. В браузере сайт не меняется ни в чём: класса `native` нет, заставки нет. Всё, что в
 * `index.css` сделано «как в приложении» (запрет выделения, переходы), висит на `html.native` — без класса оно
 * просто не срабатывает. Сторож — `src/app/nativeOnlyInBrowser.test.ts`.
 */

/** Класс на `<html>`, под которым живут правила «как в приложении» в `index.css`. */
export const NATIVE_CLASS = 'native';

export function initNativeShell(): void {
  if (!isStoreBuild()) return;
  document.documentElement.classList.add(NATIVE_CLASS);
  if (useAuthStore.persist.hasHydrated()) markReady('session');
  else useAuthStore.persist.onFinishHydration(() => markReady('session'));
}

/**
 * Заставка уходит, когда готово ОБА: восстановлен сохранённый кэш экранов и прочитана сессия.
 *
 * ⚠️ ЗАЧЕМ ЖДАТЬ КЭШ. Кэш запросов лежит в localStorage и возвращается до первого экрана — так ученик сразу видит
 * последнюю главную, урок, словарь, а свежее подтягивается в фоне. Спрячь заставку раньше — мелькнёт пустой экран
 * с крутилкой, и это та самая «вспышка», ради которой заставка держится.
 * ⚠️ ЗАЧЕМ ЖДАТЬ СЕССИЮ. В приложении она читается из защищённого хранилища асинхронно; до этого роутер не знает,
 * вошёл ли человек, и показал бы экран входа на долю секунды.
 *
 * Страховка от вечной заставки — НАТИВНАЯ (`launchAutoHide: true`, 5 с в capacitor.config.ts): если JS не
 * поднялся вовсе, прятать её было бы некому.
 */
type Part = 'cache' | 'session';
const done = new Set<Part>();
let hidden = false;

/**
 * Заставка на экране не меньше этого времени от старта страницы (слово владельца 05.10.2026, сборка 1.1.1 по шнуру:
 * «появляется на долю секунды — сделать 1–2 с»). На быстром телефоне кэш и сессия готовы за доли секунды, и лис
 * мелькал. Отсчёт — `performance.now()` от начала загрузки WebView; нативная часть запуска идёт до неё, поэтому
 * глазами выходит ≈1,5–2 с. Готовность по-прежнему обязательна: медленный телефон держит заставку дольше, а не
 * показывает пустой экран.
 */
export const SPLASH_MIN_MS = 1500;

export function markReady(part: Part): void {
  if (!isStoreBuild() || hidden) return;
  done.add(part);
  if (!done.has('cache') || !done.has('session')) return;
  hidden = true;
  // Два кадра: первый — React кладёт экран в DOM, второй — WebView его уже нарисовал. Прячем после второго,
  // иначе под уходящей заставкой на миг открылся бы ещё пустой фон.
  const hideAfterPaint = () =>
    requestAnimationFrame(() =>
      requestAnimationFrame(() => {
        void SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {
          /* заставку уже убрала нативная страховка — ничего не делаем */
        });
      }),
    );
  const wait = SPLASH_MIN_MS - performance.now();
  if (wait > 0) setTimeout(hideAfterPaint, wait);
  else hideAfterPaint();
}

/** Только для проверок. */
export function __resetNativeShellForTests(): void {
  done.clear();
  hidden = false;
}
